/**
 * Dawin Finishes design refresh — base runtime.
 *
 * Two responsibilities for Phase 0:
 *   1. Apply the chosen theme (light/dark) to <html> as early as
 *      possible to avoid FOUC. Reads from localStorage, falls back
 *      to OS preference.
 *   2. Hook IntersectionObserver for [data-reveal] elements so they
 *      fade up into view as they enter the viewport.
 *
 * Loaded as a module from layout/theme.liquid after the design
 * tokens render and the stylesheets land. Idempotent — safe to load
 * multiple times.
 *
 * Phase 1 will hang the visible dark-mode toggle button off the
 * header nav and call window.DawinTheme.toggle() / .set(). The
 * functions are defined here so the toggle is a one-liner.
 */

(function () {
  if (window.DawinTheme) return; // idempotent

  const root = document.documentElement;

  /* Light-only mode. Force-clear any prior `data-theme=dark` saved in
     localStorage from earlier builds and lock the theme attribute to
     `light`. The dark-mode CSS block in design tokens stays for
     forward-compatibility but is never activated. */
  try { localStorage.removeItem('dawin-theme'); } catch {}
  root.setAttribute('data-theme', 'light');

  window.DawinTheme = {
    get: () => 'light',
    set() {},
    toggle() {},
  };

  /* ─── Reveal-on-scroll ──────────────────────────────────────
     Adds .is-revealed when a [data-reveal] node enters the viewport.
     Honours prefers-reduced-motion by skipping the observer entirely
     and revealing everything immediately. */
  let revealObserver = null;

  function initReveal() {
    const targets = document.querySelectorAll(
      '[data-reveal]:not(.is-revealed)'
    );
    if (!targets.length) return;

    if (
      window.matchMedia('(prefers-reduced-motion: reduce)').matches ||
      !('IntersectionObserver' in window)
    ) {
      revealObserver?.disconnect();
      revealObserver = null;
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    // Re-running on shopify:section:load must not leave the previous
    // observer alive, or they accumulate across an editing session.
    revealObserver?.disconnect();

    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-revealed');
            io.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -10% 0px' }
    );
    revealObserver = io;

    targets.forEach((el) => io.observe(el));
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal, { once: true });
  } else {
    initReveal();
  }

  // Re-scan when Shopify customizer adds new sections, so editors
  // see reveal animation kick in immediately.
  document.addEventListener('shopify:section:load', initReveal);

  /* ───────────────────────────────────────────────────────────
     Off-screen animation pause (audit Sprint 03).
     Pauses long-running keyframe animations when the element
     scrolls out of view. Targets:
       - .dawin-ticker__track (40s scroll loop)
       - any element with [data-pause-offscreen]
     Honours prefers-reduced-motion globally. */
  var pauseObserver = null;

  function initOffscreenPause(){
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches){
      if (pauseObserver) { pauseObserver.disconnect(); pauseObserver = null; }
      document.querySelectorAll('.dawin-ticker__track, [data-pause-offscreen]')
        .forEach(function(el){ el.style.animationPlayState = 'paused'; });
      return;
    }
    var targets = document.querySelectorAll('.dawin-ticker__track, [data-pause-offscreen]');
    if (!targets.length || !('IntersectionObserver' in window)) return;
    /* These targets are never unobserved (the observer toggles play state on
       every enter/exit), so a duplicate would fight the original over the same
       elements. Drop the previous one before rebuilding. */
    if (pauseObserver) pauseObserver.disconnect();
    var io = new IntersectionObserver(function(entries){
      entries.forEach(function(e){
        e.target.style.animationPlayState = e.isIntersecting ? 'running' : 'paused';
      });
    }, { threshold: 0 });
    pauseObserver = io;
    targets.forEach(function(t){ io.observe(t); });
  }
  if (document.readyState === 'loading'){
    document.addEventListener('DOMContentLoaded', initOffscreenPause, { once: true });
  } else {
    initOffscreenPause();
  }
  document.addEventListener('shopify:section:load', initOffscreenPause);
})();
